import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPrint, faMoneyBillWave, faCreditCard, faMobileScreen, faArrowsRotate,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("ai2_token")}`,
  "x-hotel": localStorage.getItem("ai2_hotel") || "",
});

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtTime(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
const EGP = (n: number) => `EGP ${Math.round(n ?? 0).toLocaleString()}`;

interface Payment {
  id: number;
  bookingId: string;
  guestName: string | null;
  amount: number;
  method: string;
  note: string;
  cardLast4: string | null;
  transactionRef: string | null;
  createdAt: string;
}
interface Summary {
  cashCollected: number;  cashCount: number;
  visaCollected: number;  visaCount: number;
  instapayCollected: number; instapayCount: number;
  totalPaymentsCollected: number;
}

function methodLabel(method: string, cardLast4?: string | null) {
  const m = method?.toLowerCase();
  if (m === "visa" || m === "card") return cardLast4 ? `Visa ****${cardLast4}` : "Visa";
  if (m === "instapay") return "Instapay";
  return "Cash";
}
function methodStyle(method: string): { color: string; bg: string; border: string } {
  const m = method?.toLowerCase();
  if (m === "visa" || m === "card") return { color: "#c9a84c", bg: "rgba(201,168,76,0.12)", border: "rgba(201,168,76,0.35)" };
  if (m === "instapay") return { color: "#a855f7", bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.35)" };
  return { color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)" };
}

function printReport(type: "summary" | "transactions", date: string, payments: Payment[], summary: Summary, hotelName: string) {
  const grandTotal = payments.reduce((a, p) => a + p.amount, 0);
  const dateLabel = fmtDate(date);
  const generated = new Date().toLocaleString("en-GB");

  const summaryHtml = `
    <h2 style="margin:0 0 4px;font-size:16pt">Payment Settlement — Summary</h2>
    <p style="margin:0 0 2px;color:#555;font-size:10pt">${hotelName} · ${dateLabel} · Generated ${generated}</p>
    <hr style="margin:10px 0;border:1pt solid #ccc"/>
    <table style="width:100%;border-collapse:collapse;font-size:12pt">
      <thead><tr style="background:#f1f5f9">
        <th style="text-align:left;padding:8px 12px;border-bottom:1pt solid #ccc">Method</th>
        <th style="text-align:center;padding:8px 12px;border-bottom:1pt solid #ccc">Transactions</th>
        <th style="text-align:right;padding:8px 12px;border-bottom:1pt solid #ccc">Total Collected</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:600">Cash</td><td style="text-align:center;padding:10px 12px;border-bottom:1pt solid #e2e8f0">${summary.cashCount}</td><td style="text-align:right;padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(summary.cashCollected).toLocaleString()}</td></tr>
        <tr><td style="padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:600">Visa / Card</td><td style="text-align:center;padding:10px 12px;border-bottom:1pt solid #e2e8f0">${summary.visaCount}</td><td style="text-align:right;padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(summary.visaCollected).toLocaleString()}</td></tr>
        <tr><td style="padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:600">Instapay</td><td style="text-align:center;padding:10px 12px;border-bottom:1pt solid #e2e8f0">${summary.instapayCount}</td><td style="text-align:right;padding:10px 12px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(summary.instapayCollected).toLocaleString()}</td></tr>
        <tr style="font-weight:800;font-size:13pt;background:#f8fafc">
          <td colspan="2" style="padding:12px;border-top:2pt solid #111">Grand Total</td>
          <td style="text-align:right;padding:12px;border-top:2pt solid #111">EGP ${Math.round(grandTotal).toLocaleString()}</td>
        </tr>
      </tbody>
    </table>`;

  const transHtml = `
    <h2 style="margin:0 0 4px;font-size:16pt">Payment Settlement — All Transactions</h2>
    <p style="margin:0 0 2px;color:#555;font-size:10pt">${hotelName} · ${dateLabel} · Generated ${generated}</p>
    <hr style="margin:10px 0;border:1pt solid #ccc"/>
    <table style="width:100%;border-collapse:collapse;font-size:10pt">
      <thead><tr style="background:#f1f5f9">
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:left">#</th>
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:left">Time</th>
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:left">Guest</th>
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:right">Amount</th>
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:left">Method</th>
        <th style="padding:5px 8px;border-bottom:1pt solid #ccc;text-align:left">Note</th>
      </tr></thead>
      <tbody>
        ${payments.map((p, i) => `
          <tr>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#888">${i + 1}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#555">${fmtTime(p.createdAt)}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;font-weight:600">${p.guestName || p.bookingId || "—"}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;text-align:right;font-weight:700">EGP ${Math.round(p.amount).toLocaleString()}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0">${methodLabel(p.method, p.cardLast4)}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#888">${p.note || "—"}</td>
          </tr>`).join("")}
        <tr style="font-weight:800">
          <td colspan="3" style="padding:10px 8px;border-top:2pt solid #111;text-align:right">Total</td>
          <td style="padding:10px 8px;border-top:2pt solid #111;text-align:right">EGP ${Math.round(grandTotal).toLocaleString()}</td>
          <td colspan="2" style="border-top:2pt solid #111"></td>
        </tr>
      </tbody>
    </table>`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Settlement ${dateLabel}</title>
    <style>body{font-family:Georgia,serif;margin:20mm 18mm;color:#111}</style>
  </head><body>${type === "summary" ? summaryHtml : transHtml}</body></html>`;
  const w = window.open("", "_blank", "width=820,height=650");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

export default function SettlementPage() {
  const [date, setDate] = useState(todayStr());
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSettlement = async (d: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/dashboard/night-audit?date=${d}`, { headers: authHeaders() });
      setPayments(res.data.payments || []);
      setSummary(res.data.summary || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettlement(date); }, []);

  const hotelName = localStorage.getItem("ai2_hotel") || "Hotel";
  const grandTotal = payments.reduce((a, p) => a + p.amount, 0);

  return (
    <DashboardLayout>
      <div style={{ padding: "28px 28px 40px", minHeight: "100vh", color: "#F2EBD9" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: "#F2EBD9", margin: 0, letterSpacing: "-0.5px" }}>
              Payment Settlement
            </h1>
            <p style={{ margin: "4px 0 0", color: "rgba(242,235,217,0.4)", fontSize: 14 }}>
              Daily cash & card reconciliation — all transactions for a selected date.
            </p>
          </div>

          {/* Controls + Print buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{ background: "#0E1E38", border: "1px solid rgba(242,235,217,0.2)", borderRadius: 8, color: "#F2EBD9", padding: "8px 12px", fontSize: 14, outline: "none" }}
            />
            <button
              onClick={() => fetchSettlement(date)}
              disabled={loading}
              style={{ background: "#1a3a5c", border: "none", borderRadius: 8, padding: "8px 16px", color: "#F2EBD9", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}
            >
              <FontAwesomeIcon icon={faArrowsRotate} spin={loading} /> {loading ? "Loading…" : "Load"}
            </button>
            {summary && payments.length > 0 && (
              <>
                <button
                  onClick={() => printReport("summary", date, payments, summary, hotelName)}
                  style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 8, padding: "8px 16px", color: "#10b981", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 7 }}
                >
                  <FontAwesomeIcon icon={faPrint} /> Print Summary
                </button>
                <button
                  onClick={() => printReport("transactions", date, payments, summary, hotelName)}
                  style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.4)", borderRadius: 8, padding: "8px 16px", color: "#c9a84c", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 7 }}
                >
                  <FontAwesomeIcon icon={faPrint} /> Print Transactions
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "12px 18px", color: "#ef4444", marginBottom: 20 }}>{error}</div>
        )}

        {/* Date label */}
        {!loading && summary && (
          <div style={{ fontSize: 13, color: "rgba(242,235,217,0.4)", marginBottom: 20 }}>
            Showing transactions for <span style={{ color: "#c9a84c", fontWeight: 700 }}>{fmtDate(date)}</span>
            {" · "}<span style={{ color: "#F2EBD9", fontWeight: 600 }}>{payments.length} transaction{payments.length !== 1 ? "s" : ""}</span>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(242,235,217,0.4)" }}>Loading…</div>
        )}

        {/* Summary cards */}
        {summary && !loading && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginBottom: 28 }}>
            {[
              { label: "Cash", count: summary.cashCount, total: summary.cashCollected, color: "#10b981", icon: faMoneyBillWave },
              { label: "Visa / Card", count: summary.visaCount, total: summary.visaCollected, color: "#c9a84c", icon: faCreditCard },
              { label: "Instapay", count: summary.instapayCount, total: summary.instapayCollected, color: "#a855f7", icon: faMobileScreen },
            ].map(k => (
              <div key={k.label} style={{ background: "#0E1E38", border: `1px solid ${k.color}22`, borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: k.color + "22", display: "flex", alignItems: "center", justifyContent: "center", color: k.color, fontSize: 17 }}>
                    <FontAwesomeIcon icon={k.icon} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: k.color, background: k.color + "1a", border: `1px solid ${k.color}44`, borderRadius: 20, padding: "2px 10px" }}>
                    {k.count} tx
                  </span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{EGP(k.total)}</div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 4, fontWeight: 500 }}>{k.label}</div>
              </div>
            ))}
            <div style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Grand Total</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#F2EBD9" }}>{EGP(grandTotal)}</div>
              <div style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", marginTop: 4 }}>{payments.length} payment{payments.length !== 1 ? "s" : ""}</div>
            </div>
          </div>
        )}

        {/* Transaction log */}
        {!loading && payments.length > 0 && (
          <div style={{ background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid rgba(201,168,76,0.12)" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#F2EBD9" }}>Transaction Log</span>
            </div>
            <div style={{ padding: "8px 12px 16px" }}>
              {payments.map((p, i) => {
                const mc = methodStyle(p.method);
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 10px", borderRadius: 8, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.018)", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "rgba(242,235,217,0.25)", minWidth: 22, textAlign: "right" }}>{i + 1}</span>
                    <span style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", minWidth: 44 }}>{fmtTime(p.createdAt)}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#F2EBD9", minWidth: 120 }}>
                      {p.guestName || <span style={{ color: "rgba(242,235,217,0.35)", fontWeight: 400, fontSize: 12 }}>{p.bookingId}</span>}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: "#10b981", minWidth: 110, textAlign: "right" }}>{EGP(p.amount)}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: mc.color, background: mc.bg, border: `1px solid ${mc.border}`, borderRadius: 8, padding: "3px 10px", whiteSpace: "nowrap" }}>
                      {methodLabel(p.method, p.cardLast4)}
                    </span>
                    {p.note && <span style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", fontStyle: "italic", flex: 1 }}>{p.note}</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "12px 22px", borderTop: "1px solid rgba(201,168,76,0.12)", gap: 10 }}>
              <span style={{ color: "rgba(242,235,217,0.35)", fontSize: 13 }}>Total collected:</span>
              <span style={{ color: "#10b981", fontWeight: 900, fontSize: 18 }}>{EGP(grandTotal)}</span>
            </div>
          </div>
        )}

        {!loading && payments.length === 0 && !error && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(242,235,217,0.35)", fontSize: 14 }}>
            No payments recorded for {fmtDate(date)}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
